import dayjs from "dayjs";

// Mirrors Gizmo::Utils::DYNAMIC_VALUE_MATCHER (gizmo lib/gizmo/utils.rb) so the preview
// substitutes exactly the patterns production substitutes — including leaving labels
// that contain spaces (e.g. [[user.Renewal Date]]) untouched.
const INJECT_MATCHER = /\[\[\w+\.\w+\]\]|\[\[\w+\.\w+\.\w+\]\]/g;
const ISO_DATETIME_MATCHER = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
// Parity with Gizmo::Utils::DEFAULT_DATE_FORMAT ("%B %-d, %Y")
const DEFAULT_DATE_FORMAT = "MMMM D, YYYY";

function formatTraitValue(value, dataType) {
  if (dataType !== "date") return value;
  if (typeof value !== "string" || !ISO_DATETIME_MATCHER.test(value)) return value;
  // Parse the wall clock (offset ignored) like Ruby's DateTime.parse does on the
  // server — dayjs(value) would convert to the browser timezone and disagree with prod.
  const parsed = dayjs(value.slice(0, 19));
  // dayjs rolls over out-of-range components (e.g. 2027-13-99) instead of rejecting
  // them, unlike DateTime.parse on the server — round-trip check restores parity.
  if (!parsed.isValid() || parsed.format("YYYY-MM-DDTHH:mm") !== value.slice(0, 16)) return value;
  return parsed.format(DEFAULT_DATE_FORMAT);
}

/**
 * Resolve merchant-facing `[[...]]` inject variables in a template the same way the
 * server does (Gizmo::Utils.interpolated_value) so the admin preview matches production.
 *
 * `[[user.Label]]` resolves from `user`; date-typed labels (per `traitTypes`) with
 * ISO 8601 values render as "September 15, 2027". Missing user traits render as an
 * empty string, mirroring the server. `[[Source.item.key]]` resolves from
 * `dataSources` (dataSourcesToObject shape); unknown sources or items stay literal.
 * `[[app.X]]` is left literal (not resolvable in the preview context).
 * @param {string} template - Template text that may contain `[[...]]` injects.
 * @param {object} [context] - Resolution context.
 * @param {object} [context.user] - User traits keyed by label (previewUserContext).
 * @param {object} [context.dataSources] - Data sources keyed per dataSourcesToObject.
 * @param {object} [context.traitTypes] - Trait label => custom field data_type map.
 * @returns {string} Template with injects resolved; unresolvable patterns stay literal.
 * @example
 * resolveInjects("Renews on [[user.renewal_date]]", {
 *   user: { renewal_date: "2027-09-15T06:01:44" },
 *   traitTypes: { renewal_date: "date" },
 * }); // "Renews on September 15, 2027"
 */
export function resolveInjects(template, { user = {}, dataSources = {}, traitTypes = {} } = {}) {
  if (!template || typeof template !== "string") return template ?? "";

  return template.replace(INJECT_MATCHER, occurrence => {
    const keys = occurrence.slice(2, -2).split(".");
    const record = keys[0];
    const trait = keys[keys.length - 1];

    if (record === "user") {
      const raw = user[trait];
      return formatTraitValue(raw === null || raw === undefined ? "" : raw, traitTypes[trait]);
    }
    if (record === "app") return occurrence;

    const recordId = keys[1];
    const dataSourceItem = recordId && dataSources[record]?.[recordId];
    if (dataSourceItem === null || dataSourceItem === undefined) return occurrence;
    const value = dataSourceItem[trait];
    return value === null || value === undefined ? "" : String(value);
  });
}
